import { z } from "zod";
import { communityPublicIdentitySchema } from "./community-genesis-v1";
import { referralCodeSchema } from "./community-v1";
import { WIRE_OFFSET_DATETIME_PATTERN } from "./wire-patterns";

export const MEME_PAGE_SIZE = 24;
export const MEME_UPLOAD_MAX_BYTES = 8 * 1024 * 1024;
export const MEME_UPLOAD_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"] as const;
export const MEME_TITLE_MAX_LENGTH = 80;
export const memePublicIdSchema = z.string().regex(/^[23456789abcdefghjkmnpqrstuvwxyz]{10}$/);
export const memeTagSchema = z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{0,23}$/);
const tags = (max: number) => z.array(memeTagSchema).max(max).transform((values) => [...new Set(values)].sort());
/** Optional upload text: surrounding whitespace is dropped and blank text counts as absent. */
const optionalText = (max: number) => z.preprocess(
  (value) => typeof value === "string" && value.trim() === "" ? undefined : value,
  z.string().trim().max(max).optional(),
);
/** Only the image is required; title, alt text and tags are all optional. */
export const memeUploadMetaSchema = z.object({
  title: optionalText(MEME_TITLE_MAX_LENGTH),
  altText: optionalText(300),
  tags: tags(8).default([]),
}).strict();
/** Omitted fields are preserved; blank text clears a field. */
export const memeUpdateRequestSchema = z.object({
  title: memeUploadMetaSchema.shape.title,
  altText: memeUploadMetaSchema.shape.altText,
  tags: memeUploadMetaSchema.shape.tags.removeDefault().optional(),
}).strict().refine(
  (value) => Object.keys(value).length > 0,
  { message: "Provide at least one metadata field" },
);
export type MemeUpdateRequest = z.infer<typeof memeUpdateRequestSchema>;
export const memeQuerySchema = z.object({
  q: z.string().trim().min(1).max(100).optional(),
  tags: z.preprocess((value) => typeof value === "string" ? value.split(",") : value, tags(3).optional()),
  creator: referralCodeSchema.optional(),
  kind: z.enum(["static", "animated"]).optional(),
  period: z.enum(["24h", "7d", "30d", "all"]).default("all"),
  sort: z.enum(["new", "old", "shared", "az", "relevant"]).optional(),
  cursor: z.string().min(1).max(2048).optional(),
}).strict().superRefine((value, context) => {
  if (value.sort === "relevant" && !value.q) context.addIssue({ code: "custom", path: ["sort"], message: "Relevance requires a search" });
}).transform((value) => ({ ...value, sort: value.sort ?? (value.q ? "relevant" as const : "new" as const) }));
export const memeMediaSchema = z.object({
  full: z.string().url(), thumb: z.string().url(), poster: z.string().url(), card: z.string().url(),
}).strict();
export const memeSchema = z.object({
  publicId: memePublicIdSchema,
  title: z.string().min(1).max(80).nullable(),
  altText: z.string().min(1).max(300).nullable(),
  tags: tags(8),
  format: z.enum(["png", "jpeg", "gif", "webp"]),
  animated: z.boolean(),
  width: z.number().int().positive().max(2048),
  height: z.number().int().positive().max(2048),
  createdAt: z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN),
  shareCount: z.string().regex(/^(0|[1-9][0-9]*)$/),
  media: memeMediaSchema,
  creator: z.object({ code: referralCodeSchema, identity: communityPublicIdentitySchema }).strict(),
  /** Published from Discord by a Memelord: the credited creator may neither edit nor delete it. */
  imported: z.boolean(),
}).strict();
export const memesResponseSchema = z.object({ memes: z.array(memeSchema).max(MEME_PAGE_SIZE), nextCursor: z.string().nullable() }).strict();
export const memePermissionSchema = z.object({ canUpload: z.boolean() }).strict();
export const memeTagsResponseSchema = z.object({ tags: z.array(z.object({ tag: memeTagSchema, count: z.number().int().nonnegative() }).strict()) }).strict();
export const memeShareRequestSchema = z.object({ channel: z.enum(["x", "copy"]) }).strict();
export const memeCursorQuerySchema = z.object({ cursor: z.string().min(1).max(2048).optional() }).strict();
/** An existing live upload is linked only when it belongs to the requesting member. */
export const memeDuplicateResponseSchema = z.object({ error: z.literal("duplicate_meme"), publicId: memePublicIdSchema.optional() }).strict();
export type Meme = z.infer<typeof memeSchema>;
export type MemeQuery = z.infer<typeof memeQuerySchema>;
export type MemeUploadMeta = z.infer<typeof memeUploadMetaSchema>;

/**
 * Private `POST /internal/memes/discord` from the Discord gateway bot. Only Discord ids travel; the
 * community service re-reads the trigger message, the tagger's roles and the source post from Discord.
 */
const discordSnowflake = z.string().regex(/^[1-9][0-9]{14,20}$/);
export const MEME_DISCORD_MAX_IMAGES = 4;
export const memeDiscordImportRequestSchema = z.object({ channelId: discordSnowflake, messageId: discordSnowflake }).strict();
/** Refusals that apply to the whole post; the bot answers each with one reply. */
export const memeDiscordImportErrorSchema = z.enum([
  "not_authorized", "wrong_channel", "curator_not_linked", "poster_not_linked", "poster_disqualified",
  "poster_is_bot", "source_unavailable", "no_images", "too_many_images", "invalid_tags", "rate_limited", "discord_unavailable",
]);
export const memeDiscordImportResultSchema = z.discriminatedUnion("status", [
  z.object({ attachmentId: discordSnowflake, status: z.literal("published"), publicId: memePublicIdSchema }).strict(),
  /** This attachment of this post was imported before (repeat tag). */
  z.object({ attachmentId: discordSnowflake, status: z.literal("existing"), publicId: memePublicIdSchema.nullable() }).strict(),
  /** The same image bytes are already in the depot; `publicId` only when that meme is publicly visible. */
  z.object({ attachmentId: discordSnowflake, status: z.literal("duplicate"), publicId: memePublicIdSchema.nullable() }).strict(),
  z.object({ attachmentId: discordSnowflake, status: z.literal("rejected"), error: z.enum(["unsupported_image", "image_too_large", "media_busy", "media_unavailable"]) }).strict(),
]);
export const memeDiscordImportResponseSchema = z.union([
  z.object({ drafted: z.literal(true), posterDiscordUserId: discordSnowflake }).strict(),
  z.object({ posterDiscordUserId: discordSnowflake, results: z.array(memeDiscordImportResultSchema).min(1).max(MEME_DISCORD_MAX_IMAGES) }).strict(),
  z.object({ error: memeDiscordImportErrorSchema, posterDiscordUserId: discordSnowflake.optional() }).strict(),
]);
export type MemeDiscordImportResponse = z.infer<typeof memeDiscordImportResponseSchema>;
